import { useEffect, useState } from 'react';
import type { TaskTitlePieSlice } from '../../utils/taskHelpers';
import { TaskTitlePieSvg } from './TaskTitlePieSvg';

type MonthOption = {
  value: string;
  label: string;
};

type TaskTitlePieChartProps = {
  monthLabel: string;
  total: number;
  slices: TaskTitlePieSlice[];
  monthOptions?: MonthOption[];
  chartMonth?: string;
  onMonthChange?: (value: string) => void;
  /** When embedded in dashboard tabs, drop outer card chrome. */
  embedded?: boolean;
  /** Hide the month · count line (e.g. shown on the Tasks tab instead). */
  hideSubtitle?: boolean;
};

function useStackedPieLayout(breakpointPx = 960): boolean {
  const [stacked, setStacked] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${breakpointPx}px)`).matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpointPx}px)`);
    const onChange = () => setStacked(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [breakpointPx]);

  return stacked;
}

function MonthSelect({
  monthOptions,
  chartMonth,
  onMonthChange,
}: {
  monthOptions: MonthOption[];
  chartMonth: string;
  onMonthChange: (value: string) => void;
}) {
  return (
    <label className="task-pie__month">
      <span className="field-label">Month</span>
      <select
        className="field-input field-input--compact"
        value={chartMonth}
        onChange={(e) => onMonthChange(e.target.value)}
        disabled={monthOptions.length === 0}
      >
        {monthOptions.length === 0 ? (
          <option value="">No months</option>
        ) : (
          monthOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))
        )}
      </select>
    </label>
  );
}

export function TaskTitlePieChart({
  monthLabel,
  total,
  slices,
  monthOptions,
  chartMonth = '',
  onMonthChange,
  embedded = false,
  hideSubtitle = false,
}: TaskTitlePieChartProps) {
  const stacked = useStackedPieLayout();
  const showMonthSelect = Boolean(monthOptions && onMonthChange);
  const hasChart = total > 0 && slices.length > 0;
  const showSubtitle = !hideSubtitle;
  const ariaLabel = slices
    .map((slice) => `${slice.label} ${slice.count} (${slice.pct.toFixed(1)}%)`)
    .join(', ');

  const showTitleBlock = !embedded || showSubtitle;
  const showHead = showTitleBlock || showMonthSelect;

  return (
    <section
      className={`task-pie${embedded ? ' task-pie--embedded' : ' card'}${stacked ? ' task-pie--stacked' : ''}`}
      aria-label="Task title breakdown"
    >
      {showHead ? (
        <header className={`task-pie__head${embedded && !showSubtitle ? ' task-pie__head--month-only' : ''}`}>
          {showTitleBlock ? (
            <div>
              {!embedded ? <h2 className="task-pie__title">Task breakdown</h2> : null}
              {showSubtitle ? (
                <p className={`task-pie__subtitle${embedded ? ' task-pie__subtitle--alone' : ''}`}>
                  {hasChart
                    ? `${monthLabel} · ${total} task${total === 1 ? '' : 's'}`
                    : monthLabel}
                </p>
              ) : null}
            </div>
          ) : null}
          {showMonthSelect ? (
            <MonthSelect
              monthOptions={monthOptions!}
              chartMonth={chartMonth}
              onMonthChange={onMonthChange!}
            />
          ) : null}
        </header>
      ) : null}

      <div className="task-pie__body">
        <div
          className="task-pie__chart-wrap"
          role={hasChart ? 'img' : undefined}
          aria-label={hasChart ? ariaLabel : 'No tasks in this month'}
        >
          <TaskTitlePieSvg slices={slices} empty={!hasChart} mode={stacked ? 'stacked' : 'callouts'} />
        </div>

        {stacked && hasChart ? (
          <ul className="task-pie__legend">
            {slices.map((slice) => (
              <li key={slice.label} className="task-pie__legend-item">
                <span
                  className="task-pie__legend-swatch"
                  style={{ background: slice.color }}
                  aria-hidden
                />
                <span className="task-pie__legend-label">{slice.label}</span>
                <span className="task-pie__legend-meta">
                  {slice.count} · {slice.pct.toFixed(1)}%
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {!hasChart ? (
          <p className="task-pie__empty">
            {showMonthSelect
              ? 'No tasks in this month. Pick another month above.'
              : 'No tasks to chart for this account.'}
          </p>
        ) : null}
      </div>
    </section>
  );
}
